import { img } from "../images";
import { getDoctor } from "../doctors";
import { getBlog } from "../blogs";
import { dermatologyCategory, dermatologyServices } from "./dermatology";
import { hairCategory, hairServices } from "./hair";
import { aestheticCategory, aestheticServices } from "./aesthetic";
import { urologyCategory, urologyServices } from "./urology";
import { andrologyCategory, andrologyServices } from "./andrology";
import type {
  Service,
  ServiceCategory,
  MythFact,
  TreatmentPlan,
  ConditionType,
  DiagnosisStep,
} from "./types";

export type {
  Service,
  ServiceCategory,
  MythFact,
  TreatmentPlan,
  ConditionType,
  DiagnosisStep,
  FAQItem,
} from "./types";
export { img };

export const serviceCategories: ServiceCategory[] = [
  dermatologyCategory,
  hairCategory,
  aestheticCategory,
  urologyCategory,
  andrologyCategory,
];

/**
 * Central slug → relevant service image mapping.
 * Each service gets ONE visually relevant medical visual, so every card and
 * hero shows the exact condition/service rather than a shared generic photo.
 */
export const serviceImages: Record<string, string> = {
  // Dermatology
  "general-dermatology": img.svcGeneralDerma,
  acne: img.svcAcne,
  "acne-scars": img.svcAcneScars,
  pigmentation: img.svcPigmentation,
  melasma: img.svcMelasma,
  psoriasis: img.svcPsoriasis,
  eczema: img.svcEczema,
  "fungal-infections": img.svcFungal,
  vitiligo: img.svcVitiligo,
  urticaria: img.svcUrticaria,
  "skin-allergies": img.svcSkinAllergies,
  "nail-disorders": img.svcNailDisorders,
  "mole-skin-lesion-evaluation": img.svcMoleLesion,
  "skin-tags": img.svcSkinTags,

  // Hair & Scalp
  "hair-loss": img.svcHairLoss,
  dandruff: img.svcDandruff,
  "scalp-disorders": img.svcScalpDisorders,
  "hair-scalp-evaluation": img.svcHairScalpEval,
  "hair-health-consultation": img.svcHairHealth,

  // Urology
  "kidney-stones": img.svcKidneyStones,
  "urinary-problems": img.svcUrinaryProblems,
  "prostate-care": img.svcProstateCare,
  "male-urinary-health": img.svcMaleUrinary,
  endourology: img.svcEndourology,
  "urological-consultation": img.svcUroConsult,

  // Andrology
  "male-infertility": img.svcMaleInfertility,
  "male-sexual-health": img.svcMaleSexualHealth,
  "male-reproductive-health": img.svcMaleReproductive,
  "mens-health": img.svcMensHealth,
  "andrology-consultation": img.svcAndroConsult,

  // Aesthetic Dermatology
  "chemical-peels": img.svcChemicalPeels,
  "skin-rejuvenation": img.svcSkinRejuvenation,
  "pigmentation-peels": img.svcPigmentationPeels,
  "scar-treatment": img.svcScarTreatment,
  "cosmetic-dermatology": img.svcCosmeticDerma,
  "laser-hair-reduction": img.svcLaserHairReduction,
  "skin-brightening": img.svcSkinBrightening,
  "anti-ageing": img.svcAntiAgeing,
  "hair-growth-treatments": img.svcHairGrowth,
  "advanced-skin-procedures": img.svcAdvancedProcedures,

  // Dermatology added
  "warts-skin-tag-removal": img.svcWartsSkinTags,
  "skin-cancer-evaluation": img.svcSkinCancer,

  // Urology added
  "laser-kidney-stone-treatment": img.svcLaserKidneyStone,
  "ureteric-stone-treatment": img.svcUretericStone,
  "benign-prostatic-hyperplasia": img.svcBph,
  "urinary-retention": img.svcUrinaryRetention,
  "uti-management": img.svcUti,
  "bladder-disorders": img.svcBladderDisorders,
  "urological-cancer-care": img.svcUroCancer,
  "advanced-endourology": img.svcAdvancedEndourology,
  "laparoscopic-urology": img.svcLaparoscopic,

  // Andrology added
  "erectile-dysfunction": img.svcErectileDysfunction,
  "premature-ejaculation": img.svcPrematureEjaculation,
  "sperm-analysis": img.svcSpermAnalysis,
  "varicocele": img.svcVaricocele,
  "male-hormonal-health": img.svcMaleHormonal,
};

export const services: Service[] = [
  ...dermatologyServices,
  ...hairServices,
  ...aestheticServices,
  ...urologyServices,
  ...andrologyServices,
].map((s) => ({
  ...s,
  image: serviceImages[s.slug] ?? s.image,
}));

export const getService = (slug: string) => services.find((s) => s.slug === slug);
export const getCategory = (slug: string) => serviceCategories.find((c) => c.slug === slug);
export const servicesByCategory = (categorySlug: string) =>
  services.filter((s) => s.categorySlug === categorySlug);

/** Build a nested canonical service URL, e.g. /services/dermatology/acne */
export const serviceUrl = (service: { categorySlug: string; slug: string }) =>
  `/services/${service.categorySlug}/${service.slug}`;

/** Resolve a service by its category slug and service slug (nested route). */
export const getServiceNested = (categorySlug: string, slug: string) =>
  services.find((s) => s.categorySlug === categorySlug && s.slug === slug);

// Shared educational fallbacks so every premium section renders for all services.
const defaultRiskFactors: Record<string, string[]> = {
  dermatology: ["Family history", "Sun exposure", "Lifestyle factors", "Medical history", "Product use"],
  "hair-care": ["Genetics", "Nutrition and stress", "Hair-care habits", "Medical conditions"],
  "aesthetic-dermatology": ["Sun exposure", "Skin type and history", "Ageing", "Past skin inflammation"],
  urology: ["Age", "Lifestyle factors", "Family history", "Underlying medical conditions"],
  andrology: ["Age", "Lifestyle factors", "Family history", "Underlying medical conditions"],
};

const defaultWhenToSee = [
  "Symptoms persist or keep returning",
  "Symptoms become worse or more frequent",
  "Symptoms affect your daily activities or sleep",
  "Self-care or over-the-counter options are not helping",
  "You notice new or concerning symptoms",
];

const defaultPrevention = [
  "Follow your prescribed treatment and advice",
  "Maintain a healthy, balanced lifestyle",
  "Attend follow-up reviews as recommended",
  "Report any new or worsening symptoms early",
];

const defaultWarningSigns = [
  "Symptoms are severe or rapidly worsening",
  "New or unusual symptoms appear",
  "Signs of infection such as fever, redness or swelling",
];

export interface ServiceDetail {
  service: Service;
  category?: ServiceCategory;
  doctor?: ReturnType<typeof getDoctor>;
  symptoms: { name: string; desc: string }[];
  causes: string[];
  types: ConditionType[] | null;
  riskFactors: string[];
  whenToSee: string[];
  diagnosisSteps: DiagnosisStep[];
  treatment: TreatmentPlan | null;
  myths: MythFact[];
  prevention: string[];
  warningSigns: string[];
  relatedServices: Service[];
  relatedBlogs: ReturnType<typeof getBlog>[];
}

export function getServiceDetail(slug: string, categorySlug?: string): ServiceDetail | undefined {
  const service = categorySlug
    ? getServiceNested(categorySlug, slug)
    : getService(slug);
  if (!service) return undefined;
  const category = getCategory(service.categorySlug);
  const cat = serviceCategories.find((c) => c.slug === service.categorySlug);

  const symptoms =
    service.symptoms && service.symptoms.length > 0
      ? service.symptoms
      : service.concerns.map((c) => ({ name: c, desc: "" }));
  const causes = service.causes ?? [];
  const types = service.types && service.types.length > 0 ? service.types : null;
  const riskFactors = service.riskFactors ?? defaultRiskFactors[service.categorySlug] ?? [];
  const whenToSee = service.whenToSee ?? defaultWhenToSee;
  const diagnosisSteps = service.diagnosisSteps ?? category?.diagnosisSteps ?? [];
  const treatment = service.treatment ?? null;
  const myths = service.myths ?? category?.myths ?? [];
  const prevention = service.prevention ?? defaultPrevention;
  const warningSigns = service.warningSigns ?? defaultWarningSigns;

  const relatedMap = service.related.map((s) => getService(s)).filter(Boolean) as Service[];
  const relatedServices =
    relatedMap.length > 0 ? relatedMap : servicesByCategory(service.categorySlug).slice(0, 3);

  const relatedBlogs = (service.relatedBlogs ?? [])
    .map((b) => getBlog(b))
    .filter((b): b is NonNullable<typeof b> => Boolean(b));

  return {
    service,
    category,
    doctor: cat?.doctorSlug ? getDoctor(cat.doctorSlug) : undefined,
    symptoms,
    causes,
    types,
    riskFactors,
    whenToSee,
    diagnosisSteps,
    treatment,
    myths,
    prevention,
    warningSigns,
    relatedServices,
    relatedBlogs,
  };
}